import { useState, type MouseEvent } from 'react';
import { fmt, type AdminRevenueSeriesPointDto } from '../api';

const W = 700;
const H = 160;
const PAD_T = 10;
const PAD_B = 24;

/** SVG area/line chart with a hover crosshair — shared by the event Admin and platform SuperAdmin overview pages. */
export default function RevenueTrendChart({
  series, error,
}: { series: AdminRevenueSeriesPointDto[] | null; error: string }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (error) return <div className="form-error">{error}</div>;
  if (!series) return <div style={{ color: 'var(--muted)', fontSize: 14 }}>กำลังโหลด…</div>;
  if (series.length === 0) return <div style={{ color: 'var(--muted)', fontSize: 14 }}>ยังไม่มีข้อมูล</div>;

  const maxRevenue = Math.max(1, ...series.map((p) => p.revenue));
  const stepX = series.length > 1 ? W / (series.length - 1) : 0;
  const points = series.map((p, i) => ({ x: i * stepX, y: PAD_T + (1 - p.revenue / maxRevenue) * (H - PAD_T - PAD_B) }));
  const pathLine = points.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' ');
  const pathArea = `${pathLine} L${points[points.length - 1].x.toFixed(1)},${H - PAD_B} L0,${H - PAD_B} Z`;
  const totalRevenue = series.reduce((s, p) => s + p.revenue, 0);
  const labelEvery = Math.max(1, Math.ceil(series.length / 6));
  const hovered = hoverIdx !== null ? series[hoverIdx] : null;
  const hoveredPoint = hoverIdx !== null ? points[hoverIdx] : null;

  const handleMove = (e: MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    const idx = stepX > 0 ? Math.round(relX / stepX) : 0;
    setHoverIdx(Math.min(series.length - 1, Math.max(0, idx)));
  };

  return (
    <>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12 }}>
        รวม {fmt(totalRevenue)} ใน {series.length.toLocaleString()} วัน
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ display: 'block', overflow: 'visible', cursor: series.length > 1 ? 'crosshair' : 'default' }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <line x1="0" y1={H - PAD_B} x2={W} y2={H - PAD_B} stroke="var(--line)" strokeWidth="1" />
        <line x1="0" y1={PAD_T} x2={W} y2={PAD_T} stroke="var(--line)" strokeWidth="1" />
        <path d={pathArea} fill="var(--fg)" opacity="0.08" />
        <path d={pathLine} fill="none" stroke="var(--fg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3.5" fill="var(--fg)" />
        {hoveredPoint && (
          <>
            <line
              x1={hoveredPoint.x} y1={PAD_T} x2={hoveredPoint.x} y2={H - PAD_B}
              stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3"
            />
            <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="4" fill="var(--fg)" />
          </>
        )}
        {series.map((p, i) => (i % labelEvery === 0 || i === series.length - 1) && (
          <text
            key={p.date}
            x={points[i].x}
            y={H - 6}
            fontSize="9.5"
            fill="var(--dim)"
            textAnchor={i === 0 ? 'start' : i === series.length - 1 ? 'end' : 'middle'}
            fontFamily="var(--mono)"
          >
            {p.date.slice(5)}
          </text>
        ))}
      </svg>
      <div style={{ marginTop: 10, fontSize: 12.5, fontFamily: 'var(--mono)', color: hovered ? 'var(--fg)' : 'transparent' }}>
        {hovered ? `${hovered.date} · ${fmt(hovered.revenue)} · ${hovered.orders} ออร์เดอร์` : ' '}
      </div>
    </>
  );
}
